import { http } from '../api/http';
import type { User } from '../context/AuthContext';

export type UpdateMePayload = Partial<Pick<User, 'name' | 'first_name' | 'last_name' | 'phone' | 'rfc' | 'address' | 'city' | 'country' | 'state' | 'postal_code' | 'date_of_birth' | 'nationality' | 'bio'>>;

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  name: string;
  isDefault: boolean;
  exp?: string;
}

export type KycStatus = 'not_started' | 'pending' | 'approved' | 'rejected';
export type PldStatus = 'not_started' | 'pending' | 'approved' | 'rejected';

export interface KycMeSummary {
  kycStatus: KycStatus;
  pldStatus: PldStatus;
  lastUpdate?: string;
}

export type KycDocType = 'INE_FRONT' | 'INE_BACK' | 'ADDRESS_PROOF';

export type KycDocStatus = 'MISSING' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface KycDocStatusItem {
  docType: KycDocType;
  status: KycDocStatus;
  uploadedAt?: string;
  notes?: string;
}

export interface KycStatusesResponse {
  items: KycDocStatusItem[];
}

export interface KycHistoryItem {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  uploadedAt: string;
  notes?: string;
}

export interface KycHistoryResponse {
  items: KycHistoryItem[];
}

export interface UploadKycDocResponse {
  ok: true;
  docType: KycDocType;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  uploadedAt: string;
  kycDocumentId?: string;
}

export type KycCaseStatus =
  | 'KYC_PENDIENTE'
  | 'KYC_EN_PROCESO'
  | 'KYC_VALIDADO'
  | 'KYC_RECHAZADO'
  | 'KYC_EN_REVISION';

export interface KycCase {
  id: string;
  status: KycCaseStatus;
  incodeStatus?: string | null;
  pldStatus?: string | null;
  riskLevel?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  lastError?: string | null;
  updatedAt?: string;
}

export interface PldQuestion {
  id: string;
  question: string;
  type: 'single' | 'text' | 'boolean';
  options?: string[];
  required?: boolean;
}

export interface PldAnswerPayload {
  answers: Array<{ id: string; value: string | boolean }>;
}

export const ProfileService = {
  getMe() {
    return http<User>('/me');
  },

  updateMe(payload: UpdateMePayload) {
    return http<User>('/me', { method: 'PUT', body: payload });
  },

  updatePassword(payload: { currentPassword: string; newPassword: string }) {
    return http<{ ok: true }>('/me/password', { method: 'PUT', body: payload });
  },

  getPaymentMethods() {
    return http<PaymentMethod[]>('/me/payment-methods');
  },

  getKycSummary() {
    return http<KycMeSummary>('/me/kyc-summary');
  },

  getKycDocuments() {
    return http<KycStatusesResponse>('/v1/kyc/documents');
  },

  getKycHistory(docType: KycDocType) {
    const qs = new URLSearchParams({ docType }).toString();
    return http<KycHistoryResponse>(`/v1/kyc/documents/history?${qs}`);
  },

  async uploadKycDocument(type: KycDocType, file: File, comment?: string) {
    const form = new FormData();
    form.append('file', file);
    if (comment) form.append('comment', comment);

    const res = await fetch(`${import.meta.env.VITE_API_URL as string}/v1/kyc/documents/${type}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`
      },
      body: form
    });

    if (!res.ok) {
      const ct = res.headers.get('content-type') || '';
      const payload = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text().catch(() => '');
      const message =
        (typeof payload === 'string' && payload.trim()) ||
        (payload && typeof payload.message === 'string' ? payload.message : '') ||
        'Upload failed';
      throw new Error(message);
    }

    return (await res.json()) as UploadKycDocResponse;
  },

  getKycCase() {
    return http<{ case: KycCase | null }>('/v1/kyc/case');
  },

  getPldQuestions() {
    return http<PldQuestion[]>('/me/pld/questions');
  },

  submitPld(payload: PldAnswerPayload) {
    return http<{ ok: true }>('/me/pld/submit', { method: 'POST', body: payload });
  }
};
